<template>
    <FormModal ref="FormModal" :title="title">
      <template slot="section">
        <v-container
          class="ma-0 pa-0 fill-m-width">
          <v-row>
            <v-col
              cols="12"
              xl="6"
              lg="6"
              md="6"
              sm="6">
              <PaginatedAutocomplete
                :fetch="fetchEvents"
                :prepend-items="() => eventOptions"
                :allow-create="true"
                :commit-create-on-blur="false"
                :disabled="loading || isDimensionResolving('eventId')"
                item-text="name"
                item-value="id"
                :label="$t('procedureReport.fields.event')"
                :value="innerForm.eventId"
                @input="onFieldChange('eventId', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              xl="6"
              lg="6"
              md="6"
              sm="6">
              <PaginatedAutocomplete
                :key="`complement-${innerForm.eventId || 'none'}`"
                :fetch="fetchComplements"
                :allow-create="true"
                :commit-create-on-blur="false"
                :disabled="
                  loading
                    || !innerForm.eventId
                    || isDimensionResolving('eventId')
                    || isDimensionResolving('complementId')
                    || hasUnresolvedDimensionValue('eventId')
                "
                item-text="name"
                item-value="id"
                :label="$t('procedureReport.fields.complement')"
                :value="innerForm.complementId"
                @input="onFieldChange('complementId', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              xl="6"
              lg="6"
              md="6"
              sm="6">
              <PaginatedAutocomplete
                :fetch="fetchGroups"
                :prepend-items="() => groupOptions"
                :allow-create="true"
                :commit-create-on-blur="false"
                :disabled="loading || isDimensionResolving('groupId')"
                item-text="name"
                item-value="id"
                :label="$t('procedureReport.fields.group')"
                :value="innerForm.groupId"
                @input="onFieldChange('groupId', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              xl="6"
              lg="6"
              md="6"
              sm="6">
              <PaginatedAutocomplete
                :key="`subgroup-${innerForm.groupId || 'none'}`"
                :fetch="fetchSubGroups"
                :allow-create="true"
                :commit-create-on-blur="false"
                :disabled="
                  loading
                    || !innerForm.groupId
                    || isDimensionResolving('groupId')
                    || isDimensionResolving('subGroupId')
                    || hasUnresolvedDimensionValue('groupId')
                "
                item-text="name"
                item-value="id"
                :label="$t('procedureReport.fields.subGroup')"
                :value="innerForm.subGroupId"
                @input="onFieldChange('subGroupId', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              xl="6"
              lg="6"
              md="6"
              sm="6">
              <PaginatedAutocomplete
                :fetch="fetchPredictives"
                :prepend-items="() => predictiveOptions"
                :allow-create="true"
                :commit-create-on-blur="false"
                :disabled="loading || isDimensionResolving('predictiveId')"
                item-text="name"
                item-value="id"
                default
                :label="$t('procedureReport.fields.predictive')"
                :value="innerForm.predictiveId"
                @input="onFieldChange('predictiveId', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              xl="6"
              lg="6"
              md="6"
              sm="6">
              <PaginatedAutocomplete
                :key="`predictive-classification-${innerForm.predictiveId || 'none'}`"
                :fetch="fetchPredictiveClassifications"
                :allow-create="true"
                :commit-create-on-blur="false"
                :disabled="
                  loading
                    || !innerForm.predictiveId
                    || isDimensionResolving('predictiveId')
                    || isDimensionResolving('predictiveClassificationId')
                    || hasUnresolvedDimensionValue('predictiveId')
                "
                item-text="name"
                item-value="id"
                default
                :label="$t('procedureReport.fields.predictiveClassification')"
                :value="innerForm.predictiveClassificationId"
                @input="onFieldChange('predictiveClassificationId', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              class="d-flex flex-row gap-h-15 justify-end">
              <v-btn
                class="ui-text-base ui-btn"
                color="primary"
                outlined
                @click="cancel">
                {{ $t('common.cancel') }}
              </v-btn>

              <v-btn
                class="ui-text-base ui-btn procedure-report-form-modal__save"
                color="primary"
                :loading="loading || hasPendingDimensionRequests"
                :disabled="loading || hasPendingDimensionRequests || hasUnresolvedDimensionValues"
                @click="save">
                {{ $t('common.save') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </FormModal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import FormModal from '@/components/Modals/FormModal/FormModal.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import { translateBackendErrorMessage } from '@/store/modules/procedure-report/procedure-report-error-translator';

const DIMENSION_FIELDS = [
  'eventId',
  'complementId',
  'groupId',
  'subGroupId',
  'predictiveId',
  'predictiveClassificationId',
];

const DEPENDENT_FIELDS = {
  eventId: 'complementId',
  groupId: 'subGroupId',
  predictiveId: 'predictiveClassificationId',
};

const PARENT_FIELDS = {
  complementId: 'eventId',
  subGroupId: 'groupId',
  predictiveClassificationId: 'predictiveId',
};

export default {
  name: 'ProcedureReportFormModal',
  components: {
    FormModal,
    PaginatedAutocomplete,
  },
  data: () => ({
    innerForm: {
      eventId: null,
      groupId: null,
      subGroupId: null,
      complementId: null,
      predictiveId: null,
      predictiveClassificationId: null,
    },
    dimensionLoading: {},
    dimensionRequests: {},
    dimensionErrors: {},
  }),
  props: {
    title: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      eventOptions: 'procedure-report/eventOptions',
      complementOptions: 'procedure-report/complementOptions',
      groupOptions: 'procedure-report/groupOptions',
      subGroupOptions: 'procedure-report/subGroupOptions',
      predictiveOptions: 'procedure-report/predictiveOptions',
      predictiveClassificationOptions: 'procedure-report/predictiveClassificationOptions',
    }),
    fieldOptionsByField() {
      return {
        eventId: this.eventOptions,
        complementId: this.complementOptions,
        groupId: this.groupOptions,
        subGroupId: this.subGroupOptions,
        predictiveId: this.predictiveOptions,
        predictiveClassificationId: this.predictiveClassificationOptions,
      };
    },
    hasPendingDimensionRequests() {
      return Object.keys(this.dimensionRequests).length > 0;
    },
    hasUnresolvedDimensionValues() {
      return DIMENSION_FIELDS.some((field) => this.hasUnresolvedDimensionValue(field));
    },
  },
  methods: {
    ...mapActions({
      fetchEventOptions: 'procedure-report/fetchEventOptions',
      fetchGroupOptions: 'procedure-report/fetchGroupOptions',
      fetchSubGroupOptions: 'procedure-report/fetchSubGroupOptions',
      fetchComplementOptions: 'procedure-report/fetchComplementOptions',
      fetchPredictiveOptions: 'procedure-report/fetchPredictiveOptions',
      fetchPredictiveClassificationOptions: 'procedure-report/fetchPredictiveClassificationOptions',
      createProcedureDimension: 'procedure-report/createRecordDimension',
    }),
    ...mapMutations({ showSnackbar: 'procedure-report/showSnackbar' }),
    async fetchEvents(page, size, inputValue) {
      return this.fetchEventOptions({ page, size, inputValue });
    },
    async fetchGroups(page, size, inputValue) {
      return this.fetchGroupOptions({ page, size, inputValue });
    },
    async fetchSubGroups(page, size, inputValue) {
      return this.fetchSubGroupOptions({ page, size, inputValue, groupIds: [this.innerForm.groupId] });
    },
    async fetchComplements(page, size, inputValue) {
      return this.fetchComplementOptions({ page, size, inputValue, eventIds: [this.innerForm.eventId] });
    },
    async fetchPredictives(page, size, inputValue) {
      return this.fetchPredictiveOptions({ page, size, inputValue });
    },
    async fetchPredictiveClassifications(page, size, inputValue) {
      const payload = { page, size, inputValue, predictiveIds: [this.innerForm.predictiveId] };
      return this.fetchPredictiveClassificationOptions(payload);
    },
    open() {
      this.initForm();
      this.$refs.FormModal.open();
    },
    async save() {
      if (this.loading) return;

      await this.waitForDimensionRequests();

      if (this.hasUnresolvedDimensionValues) {
        this.showUnresolvedDimensionError();
        return;
      }

      this.$emit('save', { ...this.innerForm });
    },
    async waitForDimensionRequests() {
      const requests = Object.values(this.dimensionRequests);
      if (!requests.length) return;
      await Promise.all(requests);
    },
    resolveCreatedDimension(field, value) {
      if (!this.shouldResolveDimensionValue(field, value)) return null;

      const requestKey = this.dimensionRequestKey(field, value);
      if (this.dimensionRequests[requestKey]) {
        return this.dimensionRequests[requestKey];
      }

      this.$set(this.dimensionLoading, field, true);

      const request = this.createProcedureDimension({ field, value, record: { ...this.innerForm } })
        .then((result) => {
          if (!this.isCurrentDimensionRequest(field, value, requestKey)) return result;

          if (result?.error) {
            this.$set(this.dimensionErrors, field, result.error);
            this.showDimensionError(result.error);
            return result;
          }

          this.$delete(this.dimensionErrors, field);
          if (this.normalizeComparisonValue(result?.value) !== this.normalizeComparisonValue(value)) {
            this.$set(this.innerForm, field, result.value);
          }
          return result;
        })
        .finally(() => {
          this.$delete(this.dimensionRequests, requestKey);
          if (!this.hasPendingFieldDimensionRequest(field)) {
            this.$delete(this.dimensionLoading, field);
          }
        });

      this.$set(this.dimensionRequests, requestKey, request);
      return request;
    },
    shouldResolveDimensionValue(field, value) {
      return DIMENSION_FIELDS.includes(field)
        && this.hasDimensionValue(value)
        && typeof value === 'string'
        && !this.hasFieldOptionId(field, value);
    },
    hasDimensionValue(value) {
      return value !== null
        && value !== undefined
        && (typeof value !== 'string' || Boolean(value.trim()));
    },
    hasFieldOptionId(field, value) {
      return (this.fieldOptionsByField[field] || [])
        .some((option) => String(option.id) === String(value));
    },
    hasUnresolvedDimensionValue(field) {
      return this.shouldResolveDimensionValue(field, this.innerForm[field]);
    },
    dimensionRequestKey(field, value) {
      return [
        field,
        String(value ?? ''),
        this.dimensionParentRequestKey(field),
      ].join(':');
    },
    dimensionParentRequestKey(field) {
      const parentField = PARENT_FIELDS[field];
      return parentField ? String(this.innerForm[parentField] ?? '') : '';
    },
    hasPendingFieldDimensionRequest(field) {
      const prefix = `${field}:`;
      return Object.keys(this.dimensionRequests).some((key) => key.startsWith(prefix));
    },
    isDimensionResolving(field) {
      return Boolean(this.dimensionLoading[field]);
    },
    isCurrentDimensionRequest(field, value, requestKey) {
      return this.dimensionRequestKey(field, value) === requestKey
        && this.normalizeComparisonValue(this.innerForm[field]) === this.normalizeComparisonValue(value);
    },
    normalizeComparisonValue(value) {
      if (value === null || value === undefined) return '';
      return String(value);
    },
    showDimensionError(error) {
      this.showSnackbar({
        type: 'error',
        message: this.getDimensionErrorMessage(error),
        show: true,
      });
    },
    getDimensionErrorMessage(error) {
      const response = error?.response;
      const data = response?.data;

      if (response?.status === 403) {
        return String(this.$t('procedureReport.messages.noPermission'));
      }

      const fieldMessages = Array.isArray(data?.fields)
        ? data.fields.map((field) => field?.message).filter(Boolean).join('\n')
        : null;
      const translatedMessage = translateBackendErrorMessage(
        data?.detail || data?.message || fieldMessages || error?.message,
      );

      return String(translatedMessage || this.$t('procedureReport.messages.saveError'));
    },
    showUnresolvedDimensionError() {
      const errors = Object.values(this.dimensionErrors).filter(Boolean);
      const error = errors[0];

      if (error) {
        this.showDimensionError(error);
        return;
      }

      this.showSnackbar({
        type: 'error',
        message: String(this.$t('procedureReport.messages.saveError')),
        show: true,
      });
    },
    close() {
      this.$refs.FormModal.close();
    },
    cancel() {
      this.initForm();
      this.$refs.FormModal.close();
    },
    onFieldChange(field, value) {
      this.innerForm[field] = value;
      this.$delete(this.dimensionErrors, field);
      const dependentField = DEPENDENT_FIELDS[field];
      if (dependentField) {
        this.innerForm[dependentField] = null;
        this.$delete(this.dimensionErrors, dependentField);
      }
      this.resolveCreatedDimension(field, value);
    },
    initForm() {
      this.innerForm = {
        eventId: null,
        groupId: null,
        subGroupId: null,
        complementId: null,
        predictiveId: null,
        predictiveClassificationId: null,
      };
      this.dimensionLoading = {};
      this.dimensionRequests = {};
      this.dimensionErrors = {};
    },
  },
};
</script>

<style lang="scss" scoped>
.procedure-report-form-modal__save {
  color: var(--v-foreground-accent-base) !important;

  .v-btn__content,
  .v-btn__content * {
    color: inherit !important;
  }
}
</style>
